import { deepClone } from '@/_utils/deep-clone';
import { AdvancedStopKind, TimelineStopKind, VehicleTransport } from '@/new-transport/_models/transport.model';
import { City, RouteForVehicles, RouteStop } from '@/new-transport/_models/transport-map.model';

export const createTimelineArray = (
	routeForVehicles: RouteForVehicles,
	vehicles: VehicleTransport[],
	cities: City[],
): Map<string, { id: number; name: string; action: TimelineStopKind }[]> => {
	const usedVehiclesMap: Map<string, { id: number; name: string; action: TimelineStopKind }[]> = new Map();

	for (const [vehicleId, stops] of routeForVehicles) {
		const vehicle = vehicles.find((v) => v.id === vehicleId);
		const vehicleName = vehicle?.name;

		if (!usedVehiclesMap.has(vehicleName)) {
			usedVehiclesMap.set(vehicleName, []);
		}

		const groupedActions = groupStopsByCityChange(deepClone(stops));
		const vehicleStops = usedVehiclesMap.get(vehicleName);

		for (const key in groupedActions) {
			if (groupedActions.hasOwnProperty(key)) {
				const actionsGroup = groupedActions[key];
				const cityId = actionsGroup[0].cityId;
				const location = cities.find((loc) => loc.id === cityId);
				const actionType = determineActionType(actionsGroup);

				if (location) {
					vehicleStops.push({
						id: cityId,
						name: location.location.name,
						action: actionType,
					});
				}
			}
		}
	}

	usedVehiclesMap.forEach((value, key) => {
		if (value.length === 0) {
			usedVehiclesMap.delete(key);
		}
	});

	return usedVehiclesMap;
};

const groupStopsByCityChange = (stops: RouteStop[]): { [groupIndex: number]: RouteStop[] } => {
	return stops.reduce(
		(acc, stop, index, array) => {
			const previousStop = array[index - 1];
			const isCityChanged = !previousStop || previousStop.cityId !== stop.cityId;

			if (isCityChanged) {
				acc.groupIndex++;
				acc.groupedStops[acc.groupIndex] = [];
			}

			acc.groupedStops[acc.groupIndex].push(stop);

			return acc;
		},
		{
			groupedStops: {} as { [groupIndex: number]: RouteStop[] },
			groupIndex: 0,
		},
	).groupedStops;
};

const determineActionType = (actions: RouteStop[]): TimelineStopKind => {
	const actionTypes = new Set(actions.map((action) => action.action));
	return actionTypes.size > 1 ? AdvancedStopKind.ADVANCED : actions[0].action;
};
